import { useEffect, useState } from 'react'
import { ComponentType, DollarHandle as DollarHandleType } from '@web/graphql-components'
import { Image } from '@web/components/page-components/Image'
import { Text } from '@web/components/page-components/Text'
import { useShallow } from 'zustand/react/shallow'
import { useDonationFormContext } from '@web/components/page-components/DonationForm'
import { ComponentLayout } from '@web/components/page-components/default'
import { Box, Flex, HStack, VStack } from '@web/components/atoms/Layout'
import PageRadioButton from '@web/components/page-components/DonationForm/components/atoms/PageRadioButton'
import OtherDonateButton from '@web/components/page-components/DonationForm/components/molecules/OtherDonateButton'
import { usePageStore } from '../../../libs/hooks/usePageStore'
import { useBrandingValues } from '@web/components/utils/branding/useBrandingsStore'
import { useMergeFields } from '@web/components/page-components/Text/MergeTags/MergeTag'
import { toDisplayAmountNoDecimal } from '@web/components/utils/format'
import { useColorWithOpacity } from '@web/components/molecules/ColorPicker/utils'
import { useMediaQuery } from '@web/components/utils/chakra-hooks'
import { Nullable } from 'apps/fe-donation/utils'

type DollarHandleProps = {
  handle: DollarHandleType
  index: number
}

const DollarHandle = ({ handle, index }: DollarHandleProps) => {
  const [isMobile] = useMediaQuery(['(max-width: 900px)'], { fallback: [] })

  const styles = useDonationFormContext(({ settings }) => {
    if (isMobile) {
      const { padding, margin, ...rest } = settings.styles ?? {}

      return {
        ...rest,
        ...settings.mobileStyles,
      }
    }

    return settings.styles
  })

  const { imageUrl, description } = handle
  let { hasImage, hasDescription } = handle
  if (hasImage === undefined || hasImage === null) {
    hasImage = imageUrl !== undefined
  }
  if (hasDescription === undefined || hasDescription === null) {
    hasDescription = description !== undefined
  }

  return (
    <HStack
      className="ge-donation-form-dollar-handle"
      minH="80px"
      minW="80px"
      bgColor="ge.white"
      w="100%"
      alignItems="start"
      borderRadius={4}
    >
      {hasImage && imageUrl && (
        <Flex
          className="ge-donation-form-dollar-handle__image"
          w="100%"
          alignSelf="stretch"
          maxW={handle.description ? '80px' : '100%'}
        >
          <Image
            id="dollar-handle-img"
            type={ComponentType.Image}
            url={imageUrl}
            alt={`donation dollar-handle image`}
            styles={{
              height: '100%',
              objectFit: 'cover',
              objectPosition: styles?.[`objectPosition-${index}`] ?? '0% 50%',
            }}
          />
        </Flex>
      )}
      {hasDescription && description && (
        <Flex className="ge-donation-form-dollar-handle__description" w="100%" alignSelf="stretch" padding="10px">
          <Text id="dollar-handle-description" type={ComponentType.Text} textValue={description} />
        </Flex>
      )}
    </HStack>
  )
}

type DollarHandlesProps = {
  handles: DollarHandleType[]
  defaultHandle: Nullable<number>
  withPayment?: boolean
}
const DollarHandles = ({ handles, withPayment, defaultHandle = 0 }: DollarHandlesProps) => {
  const frequency = useDonationFormContext((state) => state.formValues.givingFrequency)
  const pageAmount = usePageStore((state) => state.amount)
  const { setFormValues, updateErrors, nextStep } = useDonationFormContext(({ actions }) => actions)

  const updateAmount = (value: number | null) => {
    setFormValues({ amount: value })
    updateErrors({ amount: null })
  }

  const { Color: colorData } = useBrandingValues()
  const { amount, isVertical } = useDonationFormContext(
    useShallow(({ settings, formValues }) => ({
      amount: formValues.amount,
      isVertical: settings.styles?.dollarHandles?.layout === ComponentLayout.Vertical,
    })),
  )
  const styles = useDonationFormContext(({ settings }) => settings.styles)
  const selectedHandleColor = useColorWithOpacity(
    styles?.selectedHandleColor,
    styles?.selectedHandleColorOpacity,
    styles?.accentColor ?? colorData['Brand'],
  )
  const selectedTextColor = useColorWithOpacity(
    styles?.selectedHandleTextColor,
    styles?.selectedHandleTextColorOpacity,
    '#FFFFFF',
  )
  const unselectedHandleColor = useColorWithOpacity(
    styles?.unselectedHandleColor,
    styles?.unselectedHandleColorOpacity,
    '#FFFFFF',
  )
  const unselectedTextColor = useColorWithOpacity(
    styles?.unselectedHandleTextColor,
    styles?.unselectedHandleTextColorOpacity,
    '#000000',
  )
  const [activeButton, setActiveButton] = useState<number | null>(defaultHandle === -1 ? null : defaultHandle)
  const activeHandle = activeButton === null ? null : handles[activeButton]
  const hasDescription = activeHandle?.hasDescription && activeHandle?.description
  const hasImage = activeHandle?.hasImage && activeHandle?.imageUrl

  useEffect(() => {
    if (amount && amount > 0 && activeButton === null) {
      setActiveButton(handles.findIndex((handle) => handle.amount === amount))
    }

    if (activeButton && handles[activeButton] && handles[activeButton].amount !== amount) {
      updateAmount(handles[activeButton].amount)
    }

    const setMergeField = useMergeFields.getState().setMergeField
    handles.forEach((handle, i) => {
      setMergeField(`DonationForm.Ask${i + 1}`, `${toDisplayAmountNoDecimal(handle.amount)}`)
    })
  }, [frequency])

  useEffect(() => {
    if (pageAmount > 0) {
      setFormValues({ amount: pageAmount })
      setActiveButton(handles.findIndex((handle) => handle.amount === pageAmount))
    }
  }, [pageAmount])

  const handleClick = (amount: number, index: number) => {
    updateAmount(amount)
    setActiveButton(index)

    // don't skip to next step if they have pay by installments
    // or if they're one step form
    if (
      withPayment ||
      styles?.options?.payByInstallmentFrequencies?.length > 0 ||
      // explicit false check here
      styles?.options?.autoAdvance === false
    ) {
      return
    }

    nextStep()
  }

  return (
    // this should be a radio and not a series of buttons
    <VStack
      className={`ge-donation-form-dollar-handles ge-donation-form-dollar-handles--${isVertical ? 'vertical' : 'horizontal'}`}
      gap="16px"
      w="100%"
    >
      {handles.length > 0 && !isVertical && (
        <HStack
          justifyContent="space-between"
          w="100%"
          css={{
            spacing: '8px',
            flexWrap: 'wrap',
            '@container page (min-width: 600px)': {
              spacing: '24px',
              flexWrap: 'nowrap',
            },
          }}
        >
          {handles.map(({ amount }, index) => {
            return (
              <Box
                key={`donation-form-handle-${frequency}-${amount}`}
                w="100%"
                css={{
                  flex: '1 1 calc(33% - 8px)',
                  '@container page (min-width: 600px)': {
                    flex: '1 1 33%',
                  },
                }}
              >
                <PageRadioButton
                  id={`dollar-handle-${amount}`}
                  buttonText={`$${toDisplayAmountNoDecimal(amount)}`}
                  onClick={() => handleClick(amount, index)}
                  styles={{
                    selectedColor: selectedHandleColor,
                    unselectedColor: unselectedHandleColor,
                    selectedTextColor: selectedTextColor,
                    unselectedTextColor: unselectedTextColor,
                    width: '100%',
                  }}
                  isActive={activeButton === index}
                />
              </Box>
            )
          })}
        </HStack>
      )}
      {!isVertical && activeHandle && (hasDescription || hasImage) && (
        <DollarHandle handle={activeHandle} index={activeButton ?? 0} />
      )}
      {isVertical &&
        handles.map((handle, index) => (
          <Box
            w="100%"
            tabIndex={0}
            key={`form-handle-${frequency}-${index}`}
            id={`dollar-handle-${handle.amount}`}
            onClick={() => handleClick(handle.amount, index)}
            border="3px solid transparent"
            css={{
              '&': {
                transition: 'all 0.2s ease-in-out',
                cursor: 'pointer',
                ...(activeButton === index ? { border: `3px solid ${selectedHandleColor}` } : {}),
              },
              '&:hover': {
                boxShadow: 'box-shadow: 0px 6px 16px 0px #0D121640',
              },
              '&:focus': {
                border: `2px solid ${selectedHandleColor}`,
              },
            }}
          >
            <DollarHandle handle={handle} index={index} />
          </Box>
        ))}
      <OtherDonateButton
        updateAmount={updateAmount}
        styles={{
          selectedColor: selectedHandleColor,
          unselectedColor: unselectedHandleColor,
          selectedTextColor: selectedTextColor,
          unselectedTextColor: unselectedTextColor,
          width: '100%',
        }}
        onClick={() => setActiveButton(-1)}
        isActive={activeButton === -1}
      />
    </VStack>
  )
}

export default DollarHandles
